import React, { useId, useRef } from 'react';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import { MarkButton } from '../MarkControls';
import * as styles from './workspace.module.css';

type Props = {
    metadata: React.ReactNode;
    validation: React.ReactNode;
    content: React.ReactNode;
    preview: React.ReactNode;
    saveAction: React.ReactNode;
    saveFeedback: React.ReactNode;
    status: string;
    previewOpen: boolean;
    onPreviewChange: (open: boolean) => void;
};
const TemplateWorkspaceLayout: React.FC<Props> = ({
    metadata,
    validation,
    content,
    preview,
    saveAction,
    saveFeedback,
    status,
    previewOpen,
    onPreviewChange
}) => {
    const previewId = useId();
    const previewRef = useRef<HTMLDivElement>(null);
    const previewTriggerRef = useRef<HTMLButtonElement>(null);
    const toggle = () => {
        const opening = !previewOpen;
        onPreviewChange(opening);
        requestAnimationFrame(() => {
            if (opening) previewRef.current?.querySelector<HTMLElement>('button')?.focus();
            else previewTriggerRef.current?.focus();
        });
    };
    return (
        <div className={styles.workspace}>
            {saveFeedback}
            {metadata}
            {validation}
            <div ref={previewRef} id={previewId} hidden={!previewOpen}>
                <Section
                    subtheme={['regular', 'background']}
                    styles={{ borderColor: 'var(--mark-border)' }}
                    border
                    noDefaultPadding
                    className={styles.panel}
                >
                    <div className={styles.canvasHeading}>
                        <H role="h6" noDefaultMargin>
                            Email preview
                        </H>
                        <MarkButton
                            compact
                            aria-expanded={previewOpen}
                            aria-controls={previewId}
                            onClick={toggle}
                        >
                            Back to editing
                        </MarkButton>
                    </div>
                    {preview}
                </Section>
            </div>
            <div hidden={previewOpen}>
                <Section
                    subtheme={['regular', 'background']}
                    styles={{ borderColor: 'var(--mark-border)' }}
                    border
                    noDefaultPadding
                    className={styles.emailCanvas}
                >
                    <div className={styles.canvasHeading}>
                        <H role="h6" noDefaultMargin>
                            Email content
                        </H>
                        <MarkButton
                            compact
                            ref={previewTriggerRef}
                            aria-expanded={previewOpen}
                            aria-controls={previewId}
                            onClick={toggle}
                        >
                            Preview
                        </MarkButton>
                    </div>
                    {content}
                </Section>
            </div>
            <div className={styles.footer}>
                <P noDefaultMargin size="small" strong role="status">
                    {status}
                </P>
                {saveAction}
            </div>
        </div>
    );
};
export default TemplateWorkspaceLayout;
